/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Checkbox, DatePicker, Flex, Typography } from '@jcm/design-system';
import { Input } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { Control, Controller, UseFormGetValues, UseFormSetValue, UseFormUnregister, useWatch } from 'react-hook-form';
import ModalContainer from '../../../../../components/modalContainer/ModalContainer';
import { TipoDadoEnum } from '../../../../../ts/enums/TipoDadoEnum';
import { TEtapa } from '../../../../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import { TObrigacao } from '../../../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import styles from './ModalAlterarData.module.scss';
import { isDataDepoisDaVencimentoObrigacao } from '../../utils/isDataDepoisDaVencimentoObrigacao';

const { TextArea } = Input;
interface ModalAlterarDataProps {
	tipoDado?: TipoDadoEnum;
	obrigacao?: TObrigacao;
	isVisible: boolean;
	onClose: () => void;
	setValue: UseFormSetValue<any>;
	getValues: UseFormGetValues<any>;
	control: Control<any>;
	unregister: UseFormUnregister<any>;
	usuarioAprovador: boolean;
	usuarioResponsavel: boolean;
	handleSubmit: (data: any) => void;
}

const ModalAlterarData: React.FC<ModalAlterarDataProps> = ({
	tipoDado,
	obrigacao,
	isVisible,
	onClose,
	control,
	getValues,
	setValue,
	unregister,
	usuarioAprovador,
	usuarioResponsavel,
	handleSubmit,
}) => {
	const [isDateChanged, setIsDateChanged] = React.useState(false);
	const etapas = obrigacao?.children;

	const justificativaNaoAprovacaoDataAlterada = useWatch({
		control,
		name: 'justificativaNaoAprovacaoDataAlterada',
	});
	const justificativaDataAlterada = useWatch({
		control,
		name: 'justificativaDataAlterada',
	});
	const aprovarAlteracaoDataFinal = useWatch({
		control,
		name: 'aprovarAlteracaoDataFinal',
	});
	const possuiSolicitacaoAlterarData = obrigacao?.pedidoAlteracaoDataFinal?.some(
		(solicitacao) => solicitacao.aprovada === 0,
	);

	const obterUltimaDataVencimento = (etapas: (TObrigacao | TEtapa)[]): Dayjs | null => {
		return etapas.reduce(
			(ultimaData, etapa) => {
				if (etapa.dataVencimento) {
					const dataEtapa = dayjs(etapa.dataVencimento);
					return !ultimaData || dataEtapa.isAfter(ultimaData) ? dataEtapa : ultimaData;
				}
				return ultimaData;
			},
			null as Dayjs | null,
		);
	};

	const isDataAntesDaUltimaData = (data: Dayjs, ultimaData: Dayjs | null) =>
		ultimaData ? data.isBefore(ultimaData, 'day') : false;

	return (
		<ModalContainer
			title='Alterar data de vencimento'
			isVisible={isVisible}
			onClose={onClose}
			className={styles.modalAlterarData}
		>
			<Flex vertical gap={2}>
				<Flex className='campoObrigatorio'>
					<Typography.Body className='campoObrigatorio'>Nova data de vencimento</Typography.Body>
				</Flex>
				<Controller
					name='novaDataFinal'
					control={control}
					render={({ field }) => (
						<div style={{ width: 'fit-content' }}>
							<DatePicker
								allowClear
								placeholder='DD/MM/YYYY'
								format='DD/MM/YYYY'
								variant='filled'
								className={styles.alterarData}
								disabledDate={(dataAtual: Dayjs) => {
									if (tipoDado === TipoDadoEnum.Obrigacao && etapas?.length) {
										const ultimaData = obterUltimaDataVencimento(etapas);
										return isDataAntesDaUltimaData(dataAtual, ultimaData);
									}

									if (tipoDado === TipoDadoEnum.Etapa) {
										return isDataDepoisDaVencimentoObrigacao(dataAtual, obrigacao);
									}

									return false;
								}}
								disabled={!usuarioResponsavel}
								defaultValue={getValues('novaDataFinal') ? dayjs(getValues('novaDataFinal')) : null}
								onChange={(e) => {
									const newDate = e ? dayjs(e).format('YYYY-MM-DD') : null;
									field.onChange(newDate);
									setValue('novaDataFinal', newDate);
									setIsDateChanged(Boolean(newDate));
								}}
								onFocus={(e) => e.target.blur()}
								name='novaDataFinal'
							/>
						</div>
					)}
				/>
			</Flex>

			<Controller
				name='justificativaDataAlterada'
				control={control}
				render={({ field }) => (
					<Flex vertical gap={2}>
						<Flex className='campoObrigatorio'>
							<Typography.Body>Justificativa para alteração da data final</Typography.Body>
						</Flex>

						<TextArea
							{...field}
							disabled={!usuarioResponsavel || (usuarioResponsavel && usuarioAprovador && possuiSolicitacaoAlterarData)}
							placeholder='Justificativa'
							allowClear
							variant='filled'
						/>
					</Flex>
				)}
			/>

			{(usuarioAprovador && !usuarioResponsavel) || (usuarioAprovador && possuiSolicitacaoAlterarData) ? (
				<>
					<Controller
						name='aprovarAlteracaoDataFinal'
						control={control}
						render={({ field }) => (
							<Flex gap={16}>
								<Flex gap={4}>
									<Checkbox
										type='checkbox'
										checked={field.value === 1}
										onChange={() => {
											setValue('aprovarAlteracaoDataFinal', field.value === 1 ? null : 1);
											setValue('estadoStatusAlterarData', 1);
										}} // 1 Status Aprovada
									/>
									<Typography.Body>Aprovar</Typography.Body>
								</Flex>
								<Flex gap={4}>
									<Checkbox
										type='checkbox'
										checked={field.value === 2}
										onChange={() => {
											setValue('aprovarAlteracaoDataFinal', field.value === 2 ? null : 2);
											setValue('estadoStatusAlterarData', 2);
										}} // 2 Status Reprovada
									/>
									<Typography.Body>Reprovar</Typography.Body>
								</Flex>
							</Flex>
						)}
					/>

					{getValues('aprovarAlteracaoDataFinal') === 2 && (
						<Controller
							name='justificativaNaoAprovacaoDataAlterada'
							control={control}
							render={({ field }) => (
								<Flex vertical gap={2}>
									<Flex className='campoObrigatorio'>
										<Typography.Body>Justificativa de Reprovação</Typography.Body>
									</Flex>
									<TextArea {...field} placeholder='Justificativa de Reprovação' allowClear variant='filled' />
								</Flex>
							)}
						/>
					)}
				</>
			) : null}

			<Flex style={{ gap: '1rem', justifySelf: 'center' }}>
				<Button
					type='filled-tonal'
					variant='error'
					onClick={() => {
						unregister('novaDataFinal');
						unregister('justificativaDataAlterada');
						unregister('solicitaAlterarDataFinal');
						onClose();
					}}
				>
					Cancelar
				</Button>

				<Button
					type='filled-tonal'
					variant='default'
					disabled={
						(usuarioAprovador &&
							aprovarAlteracaoDataFinal !== 1 &&
							!usuarioResponsavel &&
							!justificativaNaoAprovacaoDataAlterada?.trim()) ||
						((!justificativaDataAlterada?.trim() || !isDateChanged) &&
							usuarioResponsavel &&
							!(usuarioAprovador && possuiSolicitacaoAlterarData)) ||
						(usuarioAprovador &&
							possuiSolicitacaoAlterarData &&
							aprovarAlteracaoDataFinal !== 1 &&
							!justificativaNaoAprovacaoDataAlterada?.trim())
					}
					onClick={() => {
						setValue('solicitaAlterarDataFinal', true);
						onClose();
						handleSubmit(getValues());
					}}
				>
					{usuarioAprovador && possuiSolicitacaoAlterarData
						? 'Responder solicitação e salvar'
						: usuarioResponsavel && usuarioAprovador
							? 'Alterar data de vencimento e salvar'
							: usuarioResponsavel
								? 'Solicitar alteração e salvar'
								: usuarioAprovador
									? 'Responder solicitação e salvar'
									: 'Solicitar alteração e salvar'}
				</Button>
			</Flex>
		</ModalContainer>
	);
};

export default ModalAlterarData;
